// FILE: src/pages/ApprovalManager/ApprovalManagerAttendanceApprovals.js
//
// WHAT CHANGED vs YOUR CURRENT FILE:
// ─────────────────────────────────────────────────────────────────
// ORIGINAL: table had Employee, Branch, Date, Clock In, Clock Out,
//           Late Reason, Status, Action columns.
//
// NEW: added two columns:
//   1. "Selfie" — shows the clock-in selfie thumbnail using AuthenticatedImage
//      (plain <img> would fail because the endpoint requires JWT)
//      Click thumbnail → opens full-screen preview dialog
//   2. "Distance" — shows how far the employee was from the branch at clock-in
//      e.g. "34m" — so the AM can see they were physically close
//
// Both values come from clockInSelfieUrl and clockInDistanceMeters fields
// that the backend's convertToAttendanceDto() already returns.
//
// All approve/reject logic UNCHANGED.
// ─────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableHead, TableRow, TableCell,
    TableBody, Button, TextField, Stack, Chip, TableContainer,
    CircularProgress, Alert, Modal, Dialog, DialogContent, IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import AuthenticatedImage from '../../components/AuthenticatedImage';

const MODAL_STYLE = {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 420, bgcolor: 'background.paper',
    boxShadow: 24, p: 4, borderRadius: 3
};

const ApprovalManagerAttendanceApprovals = () => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ open: false, id: null, approve: true, remark: '' });
    const [previewUrl, setPreviewUrl] = useState(''); // NEW: full-screen selfie preview

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/attendance/pending-approval-manager');
            setRecords(res.data || []);
        } catch (e) {
            toast.error('Failed to load attendance records');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openModal = (id, approve) =>
        setModal({ open: true, id, approve, remark: '' });

    const submit = async () => {
        if (!modal.remark.trim()) {
            toast.error('Remark is required');
            return;
        }
        try {
            await api.put(`/attendance/approval-manager/action/${modal.id}`, null, {
                params: { approve: modal.approve, remark: modal.remark }
            });
            toast.success(modal.approve ? 'Approved (Final) — counted as present' : 'Rejected');
            setModal({ open: false, id: null, approve: true, remark: '' });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Action failed');
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={800} mb={3}>
                Attendance Approvals — First Level
            </Typography>

            {loading ? (
                <CircularProgress />
            ) : records.length === 0 ? (
                <Alert severity="success" sx={{ borderRadius: 3 }}>No pending attendance records.</Alert>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
                            <TableRow>
                                <TableCell><b>Selfie</b></TableCell>
                                <TableCell><b>Employee</b></TableCell>
                                <TableCell><b>Branch</b></TableCell>
                                <TableCell><b>Date</b></TableCell>
                                <TableCell><b>Clock In</b></TableCell>
                                <TableCell><b>Clock Out</b></TableCell>
                                <TableCell><b>Distance</b></TableCell>
                                <TableCell><b>Late Reason</b></TableCell>
                                <TableCell><b>Status</b></TableCell>
                                <TableCell><b>Action</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {records.map(r => {
                                const selfiePath = r.clockInSelfieUrl
                                    ? `/attendance/selfie/${r.clockInSelfieUrl}` : null;
                                return (
                                    <TableRow key={r.id || r.attendanceId} hover>

                                        {/* NEW: Selfie thumbnail — click to open full screen */}
                                        <TableCell>
                                            <AuthenticatedImage
                                                src={selfiePath}
                                                alt={r.employeeName}
                                                size={48}
                                                sx={{ borderRadius: 1.5 }}
                                                onClick={() => selfiePath && setPreviewUrl(selfiePath)}
                                            />
                                        </TableCell>

                                        <TableCell><b>{r.employeeName || '-'}</b></TableCell>
                                        <TableCell>{r.branchName || '-'}</TableCell>
                                        <TableCell>{r.date || '-'}</TableCell>
                                        <TableCell>{r.clockInTime || '-'}</TableCell>
                                        <TableCell>{r.clockOutTime || '-'}</TableCell>

                                        {/* NEW: GPS distance from branch at clock-in */}
                                        <TableCell>
                                            {r.clockInDistanceMeters != null
                                                ? `${Math.round(r.clockInDistanceMeters)}m`
                                                : '-'}
                                        </TableCell>

                                        <TableCell sx={{ maxWidth: 180 }}>
                                            <Typography fontSize={13} color="error.main">
                                                {r.lateReason || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={r.status} color="warning" size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Button size="small" variant="contained" color="success"
                                                    onClick={() => openModal(r.id || r.attendanceId, true)}>
                                                    Approve
                                                </Button>
                                                <Button size="small" variant="outlined" color="error"
                                                    onClick={() => openModal(r.id || r.attendanceId, false)}>
                                                    Reject
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Approve / Reject modal (unchanged) */}
            <Modal open={modal.open} onClose={() => setModal({ ...modal, open: false })}>
                <Box sx={MODAL_STYLE}>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                        {modal.approve ? '✅ Approve Attendance' : '❌ Reject Attendance'}
                    </Typography>
                    <TextField
                        fullWidth multiline rows={3}
                        label="Your Remark — Final Decision (visible to Cluster Manager in history) *"
                        value={modal.remark}
                        onChange={e => setModal({ ...modal, remark: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" fullWidth
                            onClick={() => setModal({ ...modal, open: false })}>
                            Cancel
                        </Button>
                        <Button variant="contained" fullWidth
                            color={modal.approve ? 'success' : 'error'}
                            disabled={!modal.remark.trim()}
                            onClick={submit}
                        >
                            Confirm
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            {/* NEW: Full-screen selfie preview */}
            <Dialog open={!!previewUrl} onClose={() => setPreviewUrl('')} maxWidth="sm" fullWidth>
                <DialogContent sx={{ p: 0, position: 'relative', bgcolor: '#0f172a' }}>
                    <IconButton
                        onClick={() => setPreviewUrl('')}
                        sx={{ position: 'absolute', top: 8, right: 8, color: 'white', zIndex: 1 }}
                    >
                        <Close />
                    </IconButton>
                    {previewUrl && (
                        <AuthenticatedImage
                            src={previewUrl}
                            size="100%"
                            sx={{ width: '100%', height: 'auto', maxHeight: '80vh', borderRadius: 0 }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ApprovalManagerAttendanceApprovals;
